import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';
import type { CargoProblemAttentionItem, CargoProblemListItem } from '@magimp/shared';
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  ErrorState,
  FilterPanel,
  FilterToggleButton,
  LoadingState,
  PageHeader,
  RowCard,
  UnreadBadge,
  useFilterDisclosure,
  useNarrow,
  type Column,
  type RowCardField,
} from '../../../ui/index.js';
import { formatDateTime, type AppLocale } from '../../../i18n/index.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import {
  activeCargoProblemFilterCount,
  cargoProblemQuery,
  EMPTY_CARGO_PROBLEM_FILTERS,
  type CargoProblemsFiltersState,
} from '../filters.js';
import { useCargoProblemLabels } from '../labels.js';
import {
  useCargoProblemAttention,
  useCargoProblemFilterOptions,
  useCargoProblemSummary,
  useCargoProblemsList,
} from '../queries.js';
import { CargoProblemStatusBadge } from './CargoProblemStatusBadge.js';
import { CargoProblemsFilters } from './CargoProblemsFilters.js';
import { CargoProblemsSummary } from './CargoProblemsSummary.js';
import '../cargo-problems.css';

export interface CargoProblemDraftCargo {
  id: string;
  article: string;
  nameGoods: string | null;
  transportationTariff: string | null;
  weight: string | null;
  volume: string | null;
  carrier: string | null;
}

export function CargoProblemsListPage() {
  const { t, i18n } = useTranslation('cargoProblems');
  const labels = useCargoProblemLabels();
  const navigate = useNavigate();
  const location = useLocation();
  const narrow = useNarrow();
  const disclosure = useFilterDisclosure();
  const permissions = useAuthStore((state) => state.permissions);
  const canCreate = hasPermission(permissions, 'cargoProblems.create');
  const [filters, setFilters] = useState<CargoProblemsFiltersState>(EMPTY_CARGO_PROBLEM_FILTERS);
  const query = useMemo(() => cargoProblemQuery(filters), [filters]);
  const list = useCargoProblemsList(query);
  const summary = useCargoProblemSummary(query);
  const options = useCargoProblemFilterOptions(query);
  const attention = useCargoProblemAttention();
  const locale = i18n.language as AppLocale;
  // WHY (AC-12): unread and action-required are two different asks. They are
  // rendered side by side, never merged into one number.
  const attentionByProblem = useMemo(
    () => new Map(attention.data?.items.map((item) => [item.problemId, item]) ?? []),
    [attention.data],
  );
  const columns = useMemo<Column<CargoProblemListItem>[]>(
    () => [
      {
        key: 'attention',
        label: t('list.columns.attention'),
        render: (row) => <CargoProblemAttention item={attentionByProblem.get(row.id)} />,
      },
      {
        key: 'cargo',
        label: t('list.columns.cargo'),
        render: (row) => cargoTitle(row, t('cargoUnavailable')),
      },
      {
        key: 'category',
        label: t('list.columns.category'),
        render: (row) => labels.category(row.category),
      },
      { key: 'descriptionPreview', label: t('list.columns.description') },
      {
        key: 'status',
        label: t('list.columns.status'),
        render: (row) => <CargoProblemStatusBadge status={row.status} />,
      },
      {
        key: 'assignee',
        label: t('list.columns.assignee'),
        render: (row) => row.assignee?.name ?? t('unassigned'),
      },
      {
        key: 'updatedAt',
        label: t('list.columns.updatedAt'),
        render: (row) => (
          <span className="cargo-problems-updated">
            <span>{formatDateTime(row.updatedAt, locale)}</span>
            {row.overdue ? <span className="cargo-problems-overdue">{t('overdue')}</span> : null}
          </span>
        ),
      },
      {
        key: 'tariff',
        label: t('list.columns.tariff'),
        render: (row) => row.cargo?.transportationTariff ?? '—',
      },
      {
        key: 'dimensions',
        label: t('list.columns.dimensions'),
        render: (row) =>
          row.cargo ? `${row.cargo.weight ?? '—'} / ${row.cargo.volume ?? '—'}` : '—',
      },
      {
        key: 'carrier',
        label: t('list.columns.carrier'),
        render: (row) => row.cargo?.carrier ?? '—',
      },
    ],
    [attentionByProblem, labels, locale, t],
  );
  const state = location.state as { cargoProblemDraftCargo?: CargoProblemDraftCargo } | null;
  const openCreate = () => navigate('/cargo/problems/new', { state });
  return (
    <div className="cargo-problems-page" data-testid="cargo-problems-page">
      <PageHeader
        title={t('title')}
        subtitle={list.data ? t('list.subtitle', { count: list.data.items.length }) : undefined}
        narrow={narrow}
        actions={
          <>
            <FilterToggleButton
              open={disclosure.open}
              activeCount={activeCargoProblemFilterCount(filters)}
              label={t('filters.toggle')}
              aria-controls={disclosure.panelId}
              onClick={disclosure.toggle}
            />
            {canCreate ? (
              <Button kind="primary" icon="plus" onClick={openCreate}>
                {t('actions.create')}
              </Button>
            ) : null}
          </>
        }
      />
      <FilterPanel open={disclosure.open} id={disclosure.panelId}>
        <CargoProblemsFilters
          value={filters}
          {...(options.data ? { options: options.data } : {})}
          onChange={setFilters}
        />
      </FilterPanel>
      <div className="cargo-problems-body">
        {summary.data ? (
          <CargoProblemsSummary
            summary={summary.data}
            selectedStatus={filters.status}
            selectedCategory={filters.category}
            selectedQueue={filters.queue}
            onStatus={(status) => setFilters({ ...filters, status })}
            onCategory={(category) => setFilters({ ...filters, category })}
            onQueue={(queue) => setFilters({ ...filters, queue })}
          />
        ) : null}
        {list.isLoading ? <LoadingState /> : null}
        {list.isError ? <ErrorState /> : null}
        {list.data?.hasMore ? <p className="cargo-problems-cap-note">{t('list.capped')}</p> : null}
        {list.data && list.data.items.length === 0 ? <EmptyState title={t('list.empty')} /> : null}
        {list.data && list.data.items.length > 0 ? (
          <DataTable
            columns={columns}
            rows={list.data.items}
            onSelect={(row) => navigate(`/cargo/problems/${row.id}`)}
            renderMobileCard={({ row, onSelect }) => (
              <CargoProblemCard
                row={row}
                locale={locale}
                {...(attentionByProblem.get(row.id)
                  ? { attention: attentionByProblem.get(row.id) }
                  : {})}
                onOpen={() => onSelect?.(row)}
              />
            )}
          />
        ) : null}
      </div>
      <Outlet />
    </div>
  );
}

function cargoTitle(row: CargoProblemListItem, unavailable: string): string {
  if (!row.cargo) return unavailable;
  return [row.cargo.article, row.cargo.nameGoods].filter(Boolean).join(' · ') || '—';
}

function CargoProblemAttention({ item }: { item?: CargoProblemAttentionItem | undefined }) {
  const { t } = useTranslation('cargoProblems');
  if (!item) return null;
  return (
    <span className="cargo-problem-attention">
      <UnreadBadge unreadCount={item.unreadCount} isMarkedUnread={false} />
      {item.actionRequired ? <Chip icon="flag">{t('workspace.actionRequired')}</Chip> : null}
    </span>
  );
}

function CargoProblemCard({
  row,
  locale,
  attention,
  onOpen,
}: {
  row: CargoProblemListItem;
  locale: AppLocale;
  attention?: CargoProblemAttentionItem | undefined;
  onOpen: () => void;
}) {
  const { t } = useTranslation('cargoProblems');
  const labels = useCargoProblemLabels();
  const fields: RowCardField[] = [
    { label: t('list.columns.cargo'), value: cargoTitle(row, t('cargoUnavailable')) },
    { label: t('list.columns.category'), value: labels.category(row.category) },
    { label: t('list.columns.description'), value: row.descriptionPreview },
    { label: t('list.columns.assignee'), value: row.assignee?.name ?? t('unassigned') },
    {
      label: t('list.columns.updatedAt'),
      value: [formatDateTime(row.updatedAt, locale), row.overdue ? t('overdue') : null]
        .filter(Boolean)
        .join(' · '),
    },
    { label: t('list.columns.tariff'), value: row.cargo?.transportationTariff ?? '—' },
    {
      label: t('list.columns.dimensions'),
      value: row.cargo ? `${row.cargo.weight ?? '—'} / ${row.cargo.volume ?? '—'}` : '—',
    },
    { label: t('list.columns.carrier'), value: row.cargo?.carrier ?? '—' },
  ];
  return (
    <RowCard
      ariaLabel={`${row.publicCode} ${row.descriptionPreview}`}
      header={
        <>
          <strong>{row.publicCode}</strong>
          <CargoProblemAttention {...(attention ? { item: attention } : {})} />
        </>
      }
      actions={<CargoProblemStatusBadge status={row.status} />}
      fields={fields}
      onSelect={onOpen}
    />
  );
}
